import type { MantineTheme } from '@mantine/core';
import { Box, createStyles, Group, Stack } from '@mantine/core';
import type { AIPersonaOut } from '@repo/api-codegen';
import { trimMarkdown } from '@repo/common/utils';
import { IconButton } from '@repo/foundations';
import type { ExtensionsNames } from '@repo/secoda-editor';
import { modifyOpacityOfRgbaColor } from '@repo/theme/utils';
import { observer } from 'mobx-react-lite';
import { type KeyboardEvent, useCallback, useMemo, useRef } from 'react';
import { uploadFileTransferItemsAsAttachments } from '../../../components/AIAssistant/utils';
import type { SecodaEditor } from '../../../components/Editor/SecodaEditor';
import { RichEditor } from '../../../components/RichEditor';
import { LlmStatusBannerController } from '../../LlmStatusBanner';
import type { AiChatStore } from '../store';
import { AiInputToggleButton } from './AiInputToggleButton';
import { AttachmentButton } from './AttachmentButton';
import { Attachments } from './Attachments';

const SUBMIT_ICON_WIDTH = '2rem';
const DISABLED_EXTENSIONS: ExtensionsNames[] = [
	'emoji',
	'emojimenu',
	'query_block',
	'chart_block',
	'image',
	'embed',
	'holy',
	'page',
	'attachment',
	'comment',
	'blockmenu',
	'em',
	'code_block',
	'code_fence',
];

const useStyles = createStyles((theme: MantineTheme) => ({
	container: {
		gap: 0,
		borderRadius: theme.radius.md,
		boxShadow: `0px 0px 0px 1px ${theme.other.getColor('shadow/border')},
   0px 1px 4px 0px ${modifyOpacityOfRgbaColor(theme.other.getColor('shadow/button-shadow'), 0.08)},
   0px 4px 8px 0px ${modifyOpacityOfRgbaColor(theme.other.getColor('shadow/button-shadow'), 0.08)}`,
		border: 'none',
		backgroundColor: theme.other.getColor('surface/primary/default'),
		marginRight: `calc(${theme.spacing.xs} * -1)`,
		marginLeft: `calc(${theme.spacing.xs} * -1)`,
		position: 'relative',
		// must be above PromptSuggestions
		zIndex: 2,
	},
	controls: {
		padding: `0 ${theme.spacing.xs} ${theme.spacing.xs} ${theme.spacing.xs}`,
		justifyContent: 'space-between',
		alignItems: 'flex-end',
		flexWrap: 'nowrap',
	},
	input: {
		// CSS for RichEditor
		flex: '1 !important', // The editor forces `flex-grow: 0` when in readOnly mode
		maxWidth: `calc(100% - ${SUBMIT_ICON_WIDTH})`,
		padding: theme.spacing.md,
		maxHeight: '300px',
		overflowY: 'auto',

		'&:hover,&:focus': {
			'&::-webkit-scrollbar-thumb': {
				backgroundColor: theme.other.getColor('fill/primary/selected'),
			},
		},
		// Scrollbar styling
		'&::-webkit-scrollbar': {
			backgroundColor: 'transparent',
			width: theme.spacing.xs,
		},
		'&::-webkit-scrollbar-track': {
			backgroundColor: 'transparent',
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: 'transparent',

			borderRadius: theme.radius.sm,
		},
		'&::-webkit-scrollbar-thumb:hover': {
			backgroundColor: theme.other.getColor('fill/primary/selected'),
		},
		// Outline editor
		'& .ProseMirror > *:last-child': {
			marginBottom: 0,
		},
	},
	sendTooltip: {
		backgroundColor: theme.other.getColor('fill/primary/default'),
		color: theme.other.getColor('text/primary/default'),
		borderRadius: theme.radius.sm,
		border: `1px solid ${theme.other.getColor('border/primary/default')}`,
	},
	inlineControls: {
		position: 'absolute',
		right: theme.spacing.xs,
		top: '50%',
		transform: 'translateY(-50%)',
	},
}));

function AIPromptInputInternal({
	store,
	activePersonaId,
	personas,
	canUseAiChartTool,
}: {
	store: AiChatStore;
	activePersonaId: string;
	personas: AIPersonaOut[];
	canUseAiChartTool: boolean;
}) {
	const isEmpty = !trimMarkdown(store.inputPrompt);

	const activePersona = useMemo(
		() => personas?.find((p) => p.id === activePersonaId),
		[personas, activePersonaId]
	);

	const { classes, cx } = useStyles();

	const handleSubmit = useCallback(() => {
		store.submit({
			webSearch: store.webSearch,
			chart: canUseAiChartTool ? store.chart : false,
			personaId: activePersonaId,
		});
	}, [store, activePersonaId, canUseAiChartTool]);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (!isEmpty && event.key === 'Enter' && !event.shiftKey) {
				event.preventDefault();
				handleSubmit();
			}
		},
		[isEmpty, handleSubmit]
	);

	const editorRef = useRef<SecodaEditor>(null);

	const handleMentionClick = useCallback(() => {
		if (!editorRef.current) {
			return;
		}

		editorRef.current.triggerMentionMenu();
	}, []);

	const handleClipboardPaste = useCallback(
		(e: React.ClipboardEvent<HTMLDivElement>) => {
			e.preventDefault();

			const items = [...e.clipboardData.items];
			uploadFileTransferItemsAsAttachments(items, store);
		},
		[store]
	);

	const actionIconSize = store.isSidebar ? 'sm' : 'md';

	const placeholder = useMemo(
		() =>
			activePersona?.id
				? `Ask ${activePersona.name || 'New persona'}...`
				: 'Ask Secoda AI...',
		[activePersona]
	);

	return (
		<Stack className={classes.container} spacing="xs">
			<LlmStatusBannerController />
			{!!store.attachments?.length && <Attachments store={store} />}
			<Box
				className={cx(classes.input, 'ai-prompt-input')}
				onKeyDown={handleKeyDown}
			>
				<RichEditor
					onPaste={handleClipboardPaste}
					key={`${store.inputReRenderKey}-${activePersona?.id}`}
					dataTestId="ai-prompt-input"
					initialValue={store.inputPrompt}
					emptyDocPlaceholder={placeholder}
					onChangeCallback={store.setInputPrompt}
					readOnly={false}
					disableTopGap
					disableExtensions={DISABLED_EXTENSIONS}
					singleLineEditor
					disableResourceLinking
					disableChangeCallbackDebounce
					autoFocus
					editorRef={editorRef}
				/>
			</Box>
			<Group className={classes.controls}>
				<Group spacing="3xs" style={{ flex: 1 }}>
					<IconButton
						iconName="at"
						variant="tertiary"
						onClick={handleMentionClick}
						tooltip="Mention resource or user"
						tooltipProps={{
							offset: 8,
							position: 'top',
						}}
					/>
					<AttachmentButton store={store} />
					<AiInputToggleButton
						label="Web search"
						icon="world"
						active={store.webSearch}
						onToggle={store.setWebSearch}
					/>
					{canUseAiChartTool && (
						<AiInputToggleButton
							label="Chart"
							icon="chartBar"
							active={store.chart}
							onToggle={store.setChart}
						/>
					)}
				</Group>
				<Box>
					{store.isLoading ? (
						<IconButton
							tooltip={store.isStopping ? 'Stopping...' : 'Stop'}
							tooltipProps={{
								position: 'top',
								withArrow: true,
								arrowSize: 8,
							}}
							iconName="playerStop"
							data-testid="stop-button"
							size={actionIconSize}
							onClick={store.stop}
							variant="primary"
							disabled={store.isStopping}
							loading={store.isStopping}
						/>
					) : (
						<IconButton
							variant="primary"
							iconName="arrowUp"
							data-testid="submit-button"
							disabled={isEmpty}
							size={actionIconSize}
							display="block"
							onClick={handleSubmit}
							tooltip="Send message (Enter)"
							tooltipProps={{
								position: 'top',
								withArrow: true,
								arrowSize: 8,
								disabled: isEmpty,
							}}
						/>
					)}
				</Box>
			</Group>
		</Stack>
	);
}

export const AIPromptInput = observer(AIPromptInputInternal);
